import type { BalancesDto, GroupDto, PersonDto, TransferDto } from "@caloteiros/shared";
import { formatCents } from "@caloteiros/shared";
import { useMemo, useState } from "react";
import type { ReactElement } from "react";
import { firstName, formatShortDate } from "../../lib/format";
import {
  useBalances,
  useCreateSettlement,
  useDeleteSettlement,
  useSettleAll,
  useSettlements,
} from "../../lib/queries";
import { useToast } from "../../lib/toast";
import { IconCheck, IconTrash } from "../Icons";
import { PixBox } from "../PixBox";
import { Avatar, ConfirmDialog, EmptyState, Money, Segmented, Skeleton } from "../ui";

export function SummaryTab({ group }: { group: GroupDto }): ReactElement {
  const [mode, setMode] = useState<"pairwise" | "simplified">("pairwise");
  const balances = useBalances(group.id, mode);
  const settlements = useSettlements(group.id);
  const createSettlement = useCreateSettlement(group.id);
  const deleteSettlement = useDeleteSettlement(group.id);
  const settleAll = useSettleAll(group.id);
  const toast = useToast();

  const [confirmSettleAll, setConfirmSettleAll] = useState(false);

  const peopleById = useMemo(() => {
    const map = new Map<string, PersonDto>();
    for (const person of balances.data?.people ?? []) map.set(person.id, person);
    return map;
  }, [balances.data]);

  const nameOf = (personId: string): string =>
    peopleById.get(personId)?.displayName ?? "Alguém";

  const markAsPaid = async (transfer: TransferDto): Promise<void> => {
    try {
      await createSettlement.mutateAsync({
        fromPersonId: transfer.fromPersonId,
        toPersonId: transfer.toPersonId,
        amountCents: transfer.amountCents,
        method: "PIX",
        note: undefined,
      });
      toast.success("Acerto registrado.");
    } catch {
      toast.error("Não consegui registrar o acerto.");
    }
  };

  if (balances.isLoading || !balances.data) {
    return <Skeleton height={72} count={4} />;
  }

  const data: BalancesDto = balances.data;

  return (
    <div className="stack-lg">
      <Segmented
        label="Modo de fechamento"
        value={mode}
        onChange={setMode}
        options={[
          { value: "pairwise", label: "Quem pagou" },
          { value: "simplified", label: "Menos Pix" },
        ]}
      />
      <p className="hint" style={{ marginTop: "calc(var(--space-3) * -1)" }}>
        {mode === "pairwise"
          ? "Cada um paga direto para quem colocou o dinheiro."
          : "Menor número de transferências possível para quitar o grupo."}
      </p>

      {/* Quem paga quem — a resposta que todo mundo quer. */}
      <section className="stack">
        <h2 className="section-title">Acertos pendentes</h2>
        {data.transfers.length === 0 ? (
          <div className="card">
            <EmptyState
              emoji="🎉"
              title="Tudo quitado"
              description="Ninguém deve nada a ninguém neste rolê."
            />
          </div>
        ) : (
          <div className="card">
            {data.transfers.map((transfer) => (
              <div
                key={`${transfer.fromPersonId}-${transfer.toPersonId}`}
                className="transfer-card"
              >
                <div className="transfer-line">
                  <Avatar
                    name={nameOf(transfer.fromPersonId)}
                    color={peopleById.get(transfer.fromPersonId)?.avatarColor ?? "#999"}
                    size="sm"
                  />
                  <strong>{firstName(nameOf(transfer.fromPersonId))}</strong>
                  <span className="transfer-arrow" aria-label="paga para">
                    →
                  </span>
                  <Avatar
                    name={nameOf(transfer.toPersonId)}
                    color={peopleById.get(transfer.toPersonId)?.avatarColor ?? "#999"}
                    size="sm"
                  />
                  <strong>{firstName(nameOf(transfer.toPersonId))}</strong>
                  <span className="grow" />
                  <span className="numeric" style={{ fontWeight: 700 }}>
                    {formatCents(transfer.amountCents)}
                  </span>
                </div>

                <PixBox
                  pixKey={transfer.toPixKey}
                  pixKeyType={transfer.toPixKeyType}
                  ownerName={firstName(nameOf(transfer.toPersonId))}
                />

                <button
                  type="button"
                  className="btn btn-sm btn-subtle"
                  onClick={() => void markAsPaid(transfer)}
                  disabled={createSettlement.isPending}
                >
                  <IconCheck width={16} height={16} />
                  Marcar como pago
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Saldo individual: quem está no lucro e quem está no vermelho. */}
      <section className="stack">
        <h2 className="section-title">Saldo de cada um</h2>
        <div className="card">
          {data.balances.map((row) => {
            const person = peopleById.get(row.personId);
            if (!person) return null;
            return (
              <div key={row.personId} className="balance-row">
                <Avatar name={person.displayName} color={person.avatarColor} />
                <span className="grow">
                  <span className="list-item-title">
                    {person.displayName}
                    {person.isMe ? " (você)" : ""}
                  </span>
                  <span className="faint" style={{ display: "block" }}>
                    Pagou {formatCents(row.paidCents)} · consumiu {formatCents(row.owedCents)}
                  </span>
                </span>
                <Money cents={row.balanceCents} signed />
              </div>
            );
          })}
        </div>
        <p className="hint">Total lançado no rolê: {formatCents(data.totalCents)}</p>
      </section>

      {/* Historico de acertos, com desfazer. */}
      {settlements.data && settlements.data.length > 0 ? (
        <section className="stack">
          <h2 className="section-title">Acertos já feitos</h2>
          <div className="card">
            {settlements.data.map((settlement) => (
              <div key={settlement.id} className="balance-row">
                <span className="grow">
                  <span className="list-item-title">
                    {firstName(nameOf(settlement.fromPersonId))} pagou{" "}
                    {firstName(nameOf(settlement.toPersonId))}
                  </span>
                  <span className="faint" style={{ display: "block" }}>
                    {formatShortDate(settlement.paidAt)}
                    {settlement.note ? ` · ${settlement.note}` : ""}
                  </span>
                </span>
                <span className="numeric">{formatCents(settlement.amountCents)}</span>
                <button
                  type="button"
                  className="btn-icon"
                  aria-label="Desfazer acerto"
                  onClick={() =>
                    deleteSettlement.mutate(settlement.id, {
                      onSuccess: () => toast.success("Acerto desfeito."),
                      onError: () => toast.error("Não consegui desfazer o acerto."),
                    })
                  }
                >
                  <IconTrash width={18} height={18} />
                </button>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {data.transfers.length > 0 ? (
        <button
          type="button"
          className="btn btn-ghost btn-block"
          onClick={() => setConfirmSettleAll(true)}
        >
          Fechar as contas do rolê
        </button>
      ) : null}

      <ConfirmDialog
        open={confirmSettleAll}
        title="Fechar as contas?"
        message="Vou registrar um acerto para cada pagamento pendente e marcar o rolê como quitado. Dá para desfazer acerto por acerto depois."
        confirmLabel="Fechar contas"
        onCancel={() => setConfirmSettleAll(false)}
        onConfirm={() => {
          setConfirmSettleAll(false);
          settleAll.mutate(mode, {
            onSuccess: () => toast.success("Rolê quitado!"),
            onError: () => toast.error("Não consegui fechar as contas."),
          });
        }}
      />
    </div>
  );
}
